/**
 * ReelsSection (#reels) — the Reel / short-video feature set: scripts by format,
 * the video & audio studio (incl. Reel assembly), one-idea auto-repurpose and the
 * per-network format choice. Every card maps to shipped code (videoStudio,
 * repurpose, publishing post types); availability caveat stated honestly.
 */

import { useT } from '@/i18n';
import { BrowserFrame, MediaPicture } from './MediaPicture';
import { Icon, PrimaryCta, SectionHeading, useLandingCta } from './shared';

const FEATURES = [
  { key: 'f1', icon: 'chat' },
  { key: 'f2', icon: 'play' },
  { key: 'f3', icon: 'layers' },
  { key: 'f4', icon: 'send' },
] as const;

export function ReelsSection() {
  const { t } = useT();
  const { signup } = useLandingCta();

  return (
    <section id="reels" className="relative overflow-hidden bg-surface-2/40 py-16 sm:py-20 lg:py-24">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t('routes.landing.v2.reels.eyebrow')}
          title={t('routes.landing.v2.reels.title')}
          subtitle={t('routes.landing.v2.reels.subtitle')}
        />

        <div className="mt-12 grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {FEATURES.map((f) => (
              <li key={f.key} className="rounded-token-lg border border-border bg-surface p-5 shadow-elevation-1">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-token bg-gradient-to-br from-rose-500 to-pink-500 text-white">
                  <Icon name={f.icon} className="h-5 w-5" strokeWidth={1.9} />
                </span>
                <h3 className="mt-3 font-semibold text-foreground">{t(`routes.landing.v2.reels.${f.key}Title`)}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted">{t(`routes.landing.v2.reels.${f.key}Text`)}</p>
              </li>
            ))}
          </ul>
          <BrowserFrame>
            <MediaPicture id="reel-studio" alt={t('routes.landing.v2.reels.imgAlt')} />
          </BrowserFrame>
        </div>

        <p className="mx-auto mt-8 max-w-3xl text-center text-sm leading-relaxed text-muted">
          {t('routes.landing.v2.reels.note')}
        </p>
        <div className="mt-6 flex justify-center">
          <PrimaryCta onClick={() => signup()}>{t('routes.landing.v2.reels.cta')}</PrimaryCta>
        </div>
      </div>
    </section>
  );
}
